/**
 * EIIInspectorPanel — cycle-102 sprint-1 Task 1.2.
 *
 * Renders the typed `EIIResponse` for the active region: 4 component
 * scores, the composite EII, epistemic status, trend, and rawness tags
 * per PRD F-1.2 amendment. Stale/error/missing handling per SDD §6.4.
 *
 * Pure on `data` so the page-level mount can drive it from any signal/
 * region selection. Tests exercise the four data-state branches.
 */

import { cn } from '../../../lib/utils';
import {
  buildEIIInspectorRows,
  isStale,
  type EIIResponse,
  type IntelErrorState,
} from '../../../api/intelClient';

interface EIIInspectorPanelProps {
  region: string | null;
  data: EIIResponse | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry?: () => void;
  className?: string;
}

const RAWNESS_LABELS: Record<string, string> = {
  raw: 'raw',
  collector_normalised: 'normalised',
  composite_external: 'external',
  composite_echelon_final: 'echelon-final',
};

export function EIIInspectorPanel({
  region,
  data,
  isLoading,
  error,
  onRetry,
  className,
}: EIIInspectorPanelProps) {
  // ── Empty: no region selected ────────────────────────────────────
  if (!region || region === 'global') {
    return (
      <PanelShell className={className} title="EII Inspector">
        <EmptyState message="Select a non-global region to load EII." />
      </PanelShell>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────
  if (isLoading && !data) {
    return (
      <PanelShell className={className} title="EII Inspector" region={region}>
        <Status text="Computing EII…" />
      </PanelShell>
    );
  }

  // ── Hard error (network, 4xx, 5xx — TanStack threw) ──────────────
  if (error || !data) {
    return (
      <PanelShell className={className} title="EII Inspector" region={region}>
        <FallbackChip
          kind="compute_failure"
          message={error instanceof Error ? error.message : 'EII unavailable'}
          onRetry={onRetry}
        />
      </PanelShell>
    );
  }

  // ── Soft error: server returned 200 + error_state per SDD §6.4 ────
  if (data.error_state) {
    return (
      <PanelShell
        className={className}
        title="EII Inspector"
        region={region}
        score={data.eii_score}
        status={data.epistemic_status}
        trend={data.trend}
      >
        <FallbackChip {...data.error_state} onRetry={onRetry} />
      </PanelShell>
    );
  }

  const rows = buildEIIInspectorRows(data);
  const stale = isStale(data.freshness_seconds);

  return (
    <PanelShell
      className={className}
      title="EII Inspector"
      region={region}
      score={data.eii_score}
      status={data.epistemic_status}
      trend={data.trend}
      stale={stale}
      freshnessSeconds={data.freshness_seconds}
    >
      <table
        aria-label={`EII component breakdown for ${region}`}
        className="w-full text-[11px] font-mono"
      >
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--e-text-muted)]">
            <th className="pb-2 font-medium">Component</th>
            <th className="pb-2 font-medium text-right">Value</th>
            <th className="pb-2 font-medium text-right">Tier</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.label}
              data-testid={`eii-row-${row.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                'border-t border-[var(--e-border-secondary)]/40',
                row.contextOnly && 'bg-[var(--e-bg-elevated)]/40',
              )}
            >
              <td className="py-1.5 pr-2 text-[var(--e-text-secondary)]">
                {row.label}
                {row.contextOnly && (
                  <span
                    className="ml-2 text-[9px] uppercase tracking-wider text-[var(--e-text-muted)]"
                    data-testid={`eii-context-only-${row.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    context-only — not weighted in EII
                  </span>
                )}
              </td>
              <td className="py-1.5 pr-2 text-right text-[var(--e-text-primary)]">
                {formatValue(row.value)}
              </td>
              <td className="py-1.5 text-right text-[var(--e-text-muted)]">
                {RAWNESS_LABELS[row.derivationLevel] ?? row.derivationLevel}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </PanelShell>
  );
}

// ── Sub-components ────────────────────────────────────────────────

interface PanelShellProps {
  title: string;
  region?: string;
  score?: number;
  status?: string;
  trend?: 'Stable' | 'Escalating' | 'De-escalating';
  stale?: boolean;
  freshnessSeconds?: number;
  className?: string;
  children: React.ReactNode;
}

function PanelShell({
  title,
  region,
  score,
  status,
  trend,
  stale,
  freshnessSeconds,
  className,
  children,
}: PanelShellProps) {
  return (
    <section
      aria-label={title}
      className={cn(
        'flex flex-col gap-3 rounded-lg border border-[var(--e-border-secondary)] bg-[var(--e-bg-card)]/85 backdrop-blur-md p-4',
        className,
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]">
            {title}
          </span>
          {region && (
            <span className="text-[12px] font-mono uppercase tracking-wider text-[var(--e-text-primary)]">
              {region}
            </span>
          )}
        </div>
        {typeof score === 'number' && (
          <div className="flex flex-col items-end gap-1">
            <span
              className="text-[28px] font-mono leading-none text-[var(--e-text-primary)]"
              data-testid="eii-composite"
            >
              {score}
            </span>
            <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-wider">
              {status && (
                <span className="px-1.5 py-0.5 rounded-sm border border-[var(--e-border-secondary)] text-[var(--e-text-secondary)]">
                  {status}
                </span>
              )}
              {trend && <TrendChip trend={trend} />}
              {stale && (
                <span
                  className="px-1.5 py-0.5 rounded-sm border border-amber-500/50 text-amber-500"
                  data-testid="eii-stale-chip"
                >
                  stale · {freshnessSeconds}s
                </span>
              )}
            </div>
          </div>
        )}
      </header>
      {children}
    </section>
  );
}

function TrendChip({ trend }: { trend: 'Stable' | 'Escalating' | 'De-escalating' }) {
  const arrow = trend === 'Escalating' ? '↑' : trend === 'De-escalating' ? '↓' : '→';
  return (
    <span className="px-1.5 py-0.5 rounded-sm border border-[var(--e-border-secondary)] text-[var(--e-text-secondary)]">
      {arrow} {trend.toLowerCase()}
    </span>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <p
      role="status"
      className="text-[11px] font-mono text-[var(--e-text-muted)]"
    >
      {message}
    </p>
  );
}

function Status({ text }: { text: string }) {
  return (
    <p
      role="status"
      aria-live="polite"
      className="text-[11px] font-mono text-[var(--e-text-muted)]"
    >
      {text}
    </p>
  );
}

interface FallbackChipProps {
  kind: NonNullable<IntelErrorState>['kind'];
  message: string;
  onRetry?: () => void;
}

function FallbackChip({ kind, message, onRetry }: FallbackChipProps) {
  const label =
    kind === 'stale'
      ? 'Stale data'
      : kind === 'missing_input'
        ? 'Not computable'
        : 'Compute failure';

  return (
    <div
      role="alert"
      data-testid={`eii-fallback-${kind}`}
      className="flex flex-col gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 px-3 py-2"
    >
      <span className="text-[10px] font-mono uppercase tracking-wider text-amber-500">
        {label}
      </span>
      <p className="text-[11px] text-[var(--e-text-secondary)]">{message}</p>
      {onRetry && kind !== 'missing_input' && (
        <button
          type="button"
          onClick={onRetry}
          className="self-start px-2 py-1 text-[10px] font-mono uppercase tracking-wider rounded border border-[var(--e-border-secondary)] text-[var(--e-text-secondary)] hover:text-[var(--e-text-primary)] hover:bg-[var(--e-bg-elevated)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--e-purple-400)]"
        >
          Retry
        </button>
      )}
    </div>
  );
}

function formatValue(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2);
}
